/**
 * The adapter between the wire and `@annwn/ui` for the in-play HUD (RC2-07).
 *
 * Everything here is a pure function over wire types, for the reason the
 * condition adapter is: "no number reaches the screen" and "a slot never claims
 * a verb the server refused" are *rules*, and a rule you can only check by
 * rendering is a rule nobody checks.
 *
 * ## Bars without hit points
 *
 * The approved reference draws two meters at the top left, and the combat
 * bible's second law is that there are none: *"No hit points. Characters do not
 * grind down a health bar."* Both are satisfied by the same move — the meter's
 * `displayValue` takes a **word**, so Body reads `pale` and Breath reads
 * `labouring` where the study drew `17/19` and `12/16`.
 *
 * That is not a compromise between the two, it is what the game already knows:
 * blood is a real quantity the sim tracks and the ladder it sits on is what a
 * bystander can see, so a bar of it is honest where a bar of abstract health
 * would be a lie. Nothing in this file formats a numeral, and a test holds it
 * there.
 *
 * ## The hotbar is the keymap
 *
 * The reference's bottom row is drawn with slots keyed `1`–`6`. Annwn's verbs
 * are already bound to letters a player's hand is resting on, and a second set
 * of numeric bindings would be a second thing to learn and a second thing to
 * rebind. So the slots carry the **real** bindings, which makes the bar do a
 * job the study did not ask for: it is the always-visible key legend, and it
 * retires the wall of instructional text the client used to dump into the log
 * on the first frame.
 *
 * A slot is greyed with the server's own sentence when the verb is refused —
 * the same "greyed, not hidden" rule the camp menu keeps, for the same reason:
 * *"there is a fire here already"* is the game explaining itself at the one
 * moment somebody wants to know.
 */

import type {
  AffordanceSnapshot,
  NeedsSnapshot,
  SelfCombatSnapshot,
  SelfSnapshot,
  SkySnapshot,
} from '@annwn/protocol';
import { formatGameClock, quarter, weatherName, type WeatherKind } from '@annwn/protocol';
import type { MeterVariant, StatusTagTone } from '@annwn/ui';

import { badgesFor, channelProse, conditionSentence } from './condition-view-model.js';
import { sightProse, wetnessProse } from './sky-line.js';

/* -------------------------------------------------------------------------- *
 * Who
 * -------------------------------------------------------------------------- */

export interface HudWho {
  readonly name: string;
  /** `Heliwr · of Cwm Twrch`, or `''` when the background is not known. */
  readonly line: string;
}

/**
 * The name at the far left of the strip, and the trade under it.
 *
 * Read off `self.sheet.identity` rather than the connection's name: the sheet's
 * copy is the one the server resolved against the character record, so the strip
 * and the character sheet cannot disagree about who is playing.
 */
export function hudWho(self: SelfSnapshot | null): HudWho | null {
  if (!self) return null;
  const identity = self.sheet?.identity;
  const name = identity?.name ?? self.name;
  if (!name) return null;

  // The trade is the useful half of the blurb at strip size; the whole sentence
  // lives on the character sheet, one key away.
  return { name, line: identity?.trade ?? '' };
}

/* -------------------------------------------------------------------------- *
 * The meters
 * -------------------------------------------------------------------------- */

export interface HudMeter {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly max: number;
  /** A word, never a count. See the note at the top of the file. */
  readonly displayValue: string;
  readonly variant: MeterVariant;
}

/**
 * Blood, as the one word a bystander would use. Shorter than the condition
 * panel's clause (`pale, and cold at the fingers`) because a strip meter has a
 * label's worth of room and the long form is a sentence away in the panel.
 */
const BLOOD_WORD: Readonly<Record<string, string>> = {
  hale: 'hale',
  pale: 'pale',
  failing: 'failing',
  dying: 'dying',
  dead: 'gone',
};

/** Wind, as the sim's own band. Already a word; listed so an unknown one degrades. */
const WIND_WORD: Readonly<Record<string, string>> = {
  fresh: 'fresh',
  labouring: 'labouring',
  winded: 'winded',
};

/**
 * The two meters, in the order the reference draws them.
 *
 * Body turns blood-red below the halfway mark rather than at a fixed stage: the
 * `danger` variant pulses, and a pulsing bar is a thing you should look at, not
 * a thing you should get used to.
 */
export function hudMeters(combat: SelfCombatSnapshot | null): readonly HudMeter[] {
  if (!combat) return [];

  const blood = Math.max(0, Math.min(100, combat.blood));
  const wind = Math.max(0, Math.min(1, combat.stamina));

  return [
    {
      id: 'body',
      label: 'Body',
      value: blood,
      max: 100,
      displayValue: BLOOD_WORD[combat.bloodStage] ?? combat.bloodStage,
      variant: blood <= 50 ? 'danger' : 'life',
    },
    {
      id: 'breath',
      label: 'Breath',
      value: Math.round(wind * 100),
      max: 100,
      displayValue: WIND_WORD[combat.band] ?? combat.band,
      variant: 'breath',
    },
  ];
}

/* -------------------------------------------------------------------------- *
 * The tags
 * -------------------------------------------------------------------------- */

export interface HudTag {
  readonly id: string;
  readonly label: string;
  readonly tone: StatusTagTone;
}

/**
 * Tones for the momentary states, shared with the condition panel's own mapping
 * so one body never wears two different colours for one fact.
 */
const MOMENTARY_TONES: Readonly<Record<string, StatusTagTone>> = {
  down: 'bleed',
  prone: 'bleed',
  staggered: 'bleed',
  guarding: 'life',
  relieved: 'myth',
};

/**
 * States the Breath meter already reads out, and which must not also be a chip.
 *
 * The condition panel wanted `winded` and `labouring` as tags because it had no
 * meter to put them on. The strip does, and it says the same word in the same
 * glance — a fact drawn twice is a row of chips a player learns to stop reading.
 */
const ON_THE_BREATH_METER: ReadonlySet<string> = new Set(['winded', 'labouring']);

/** Hunger, when there is anything to say. `fed` is the common case and is silent. */
function hungerTag(needs: NeedsSnapshot): HudTag | null {
  if (needs.hunger === 'starving') return { id: 'hunger', label: 'Starving', tone: 'bleed' };
  if (needs.hunger === 'hungry') return { id: 'hunger', label: 'Hungry', tone: 'warn' };
  return null;
}

/**
 * Where you are standing, as a place to stop.
 *
 * Only the two ends of the ladder get a chip: a hearth is worth knowing about
 * and so is having nowhere at all, and "dry ground" is the default state of the
 * valley — a tag that is true most of the time is a tag nobody reads.
 */
function shelterTag(needs: NeedsSnapshot): HudTag | null {
  if (needs.resting) {
    if (needs.rest === 'cot') return { id: 'shelter', label: 'In the cot', tone: 'life' };
    if (needs.rest === 'bed')
      return { id: 'shelter', label: 'Sleeping by the hearth', tone: 'life' };
    if (needs.rest === 'fire')
      return { id: 'shelter', label: 'Sleeping by the fire', tone: 'life' };
    return { id: 'shelter', label: 'Lying down', tone: 'life' };
  }
  // `bed` is the historical sim tier for a roofed floor beside a village
  // hearth; the only literal bed is the Meddyg's `cot` tier. Say what is
  // actually on the map instead of promising furniture that is not there.
  if (needs.rest === 'bed') return { id: 'shelter', label: 'By the hearth', tone: 'life' };
  if (needs.rest === 'exposed') return { id: 'shelter', label: 'Exposed', tone: 'cold' };
  return null;
}

/**
 * The chips along the strip: what is true about this body right now, worst
 * first.
 *
 * Momentary states lead because they change how the next second goes; the
 * standing needs follow because they change how the next hour does. The order
 * never varies, so a player learns to skim it rather than read it.
 */
export function hudTags(self: SelfSnapshot | null): readonly HudTag[] {
  if (!self) return [];

  const tags: HudTag[] = [];

  if (self.combat) {
    for (const state of badgesFor(self.combat)) {
      if (ON_THE_BREATH_METER.has(state.id)) continue;
      // Rest uses combat's prone posture so movement and vulnerability share
      // one rule. It is not, however, a knockdown: the shelter tag below names
      // the cot, hearth or ground the player deliberately lay on.
      if (state.id === 'prone' && self.needs.resting) continue;
      tags.push({
        id: state.id,
        label: capitalize(state.label),
        tone: MOMENTARY_TONES[state.id] ?? 'neutral',
      });
    }
  }

  const hunger = hungerTag(self.needs);
  if (hunger) tags.push(hunger);

  if (self.needs.fire) tags.push({ id: 'fire', label: 'At a fire', tone: 'warn' });

  const shelter = shelterTag(self.needs);
  if (shelter) tags.push(shelter);

  return tags;
}

/**
 * The line under the tags: what you are doing, or how you are.
 *
 * A running channel takes it while it lasts — whether you are still knelt over
 * the wound is the more urgent question — and otherwise it is the condition
 * sentence, which is the same one the condition panel shows.
 */
export function hudProse(combat: SelfCombatSnapshot | null): string {
  if (!combat) return '';
  return channelProse(combat) ?? conditionSentence(combat);
}

/** The next observable step of the oldest active errand. */
export function hudObjective(self: SelfSnapshot | null): string {
  const errand = self?.sheet.errands[0];
  if (!errand) return '';
  const next = errand.objectives.find((one) => one.current < one.count);
  if (!next) return errand.prompt;
  // Compact one-trigger errands inherit static objective text, while `prompt`
  // is rendered live with facts such as “Nest is 12 steps north-west”.
  if (errand.objectives.length === 1 && next.id === 'complete') return errand.prompt;
  return next.count > 1 ? `${next.text} (${next.current}/${next.count})` : next.text;
}

/* -------------------------------------------------------------------------- *
 * The clock
 * -------------------------------------------------------------------------- */

export interface HudClock {
  /** `First light`, `Noon` — the hour, spelled the way the sim spells it. */
  readonly time: string;
  /** `drizzle · a south-west wind` — what the sky is doing. */
  readonly weather: string;
  /** `the ground is damp` — the one thing about the ground worth a glance. */
  readonly ground: string;

  /**
   * "You can barely see your own hands", or `''`.
   *
   * M3-05's sight warning, kept when its strip was retired: being unable to see
   * is the one weather fact that changes what a player should do next, and the
   * brand answers it in the same breath — being told you cannot see and then
   * told you are holding a light is one thought, not two.
   */
  readonly warning: string;

  /** Carrying something burning; the clock takes the fire colour when true. */
  readonly lit: boolean;
}

/**
 * The right-hand end of the strip (M3-05's sky, at strip size).
 *
 * The clock is the one number the design allows and it earns it: a player
 * planning to be off the moor before dark has to be able to ask how long they
 * have, and "the light is going" does not answer that.
 */
export function hudClock(sky: SkySnapshot | null): HudClock | null {
  if (!sky) return null;

  const sight = sightProse(sky.sight);
  return {
    time: formatGameClock(sky),
    // The wire carries the id as a plain string — a client compiled against an
    // older protocol must not choke on a sky it has never heard of.
    weather: `${weatherName(sky.weather as WeatherKind)} · a ${quarter(sky.wind)} wind`,
    ground: `the ground is ${wetnessProse(sky.wetness)}`,
    warning: sight === null ? '' : sky.lit ? `${sight}, even with the brand.` : `${sight}.`,
    lit: sky.lit,
  };
}

/* -------------------------------------------------------------------------- *
 * The hotbar
 * -------------------------------------------------------------------------- */

/** Which half of the bar a slot belongs to. */
export type HudSlotGroup = 'verb' | 'panel';

export interface HudSlot {
  /** The keymap `Action.kind` this slot runs. `Game` dispatches on it. */
  readonly id: string;
  /** The letter actually bound to it, shown on the chip. */
  readonly key: string;
  readonly label: string;
  /** The note, the reason, or the target — one short clause, never hover-only. */
  readonly meta?: string;
  /** A stance that is currently held. Lit, and the label says so too. */
  readonly on?: boolean;
  readonly disabled?: boolean;
  readonly group: HudSlotGroup;
}

/**
 * What `Game` knows and the snapshot does not: which overlays are open, whether
 * the look cursor is out, and who is standing next to you.
 */
export interface HudState {
  readonly looking?: boolean;
  /** The shared cursor is choosing a bow target, rather than inspecting. */
  readonly targeting?: boolean;
  readonly satchelOpen?: boolean;
  readonly sheetOpen?: boolean;
  readonly battleOpen?: boolean;
  /** The name of whoever is within reach, for the engage slot's meta. */
  readonly adjacent?: string | null;
  /** The opponent already engaged with the viewer, for the strike slot's meta. */
  readonly engaged?: string | null;
  /** The actor held in the bow's sight, until the shot or cancellation. */
  readonly rangedTarget?: string | null;
  /** Something is underfoot worth gathering — the client can see this itself. */
  readonly floraUnderfoot?: string | null;
}

function offerFor(
  affordances: readonly AffordanceSnapshot[] | undefined,
  verb: string,
): AffordanceSnapshot | undefined {
  return affordances?.find((offer) => offer.verb === verb);
}

/**
 * A slot built from a server affordance: available with its note, or greyed
 * with the sentence saying why not.
 */
function verbSlot(
  id: string,
  key: string,
  label: string,
  offer: AffordanceSnapshot | undefined,
  fallbackReason: string,
): HudSlot {
  if (!offer) {
    return { id, key, label, meta: fallbackReason, disabled: true, group: 'verb' };
  }
  if (!offer.available) {
    return { id, key, label, meta: offer.reason ?? 'not now', disabled: true, group: 'verb' };
  }
  return offer.note
    ? { id, key, label, meta: offer.note, group: 'verb' }
    : { id, key, label, group: 'verb' };
}

/**
 * The bar, in one fixed order.
 *
 * Fixed on purpose: a bar whose slots move as the world changes is a bar nobody
 * builds muscle memory for — the same argument `VERB_KEYS` makes for binding a
 * key per verb rather than per position. Slots go dim, they never reshuffle.
 */
export function hudSlots(self: SelfSnapshot | null, state: HudState = {}): readonly HudSlot[] {
  const affordances = self?.affordances;
  const combat = self?.combat ?? null;

  const gather = verbSlot(
    'forage',
    'f',
    'Gather',
    offerFor(affordances, 'forage'),
    state.floraUnderfoot ? 'wait' : 'nothing here',
  );

  const light = verbSlot(
    'kindle',
    'r',
    'Light',
    offerFor(affordances, 'kindle'),
    'nothing to burn',
  );

  const camp: HudSlot = {
    id: 'camp',
    key: 'c',
    label: 'Camp',
    group: 'verb',
    ...(self?.needs.fire ? { meta: 'at the fire' } : {}),
    ...(self && self.affordances.length === 0 ? { disabled: true, meta: 'nothing to do' } : {}),
  };

  const stalk: HudSlot = {
    id: 'sneak',
    key: 's',
    label: 'Stalk',
    group: 'verb',
    ...(combat?.sneaking ? { on: true, meta: 'on' } : {}),
  };

  const guard: HudSlot = {
    id: 'guard',
    key: 'g',
    label: 'Guard',
    group: 'verb',
    ...(combat?.guarding ? { on: true, meta: 'up' } : {}),
  };

  // Ranged and melee are the same slot, because they are the same intention and
  // only one of them is ever possible: the bow in your hands decides which.
  const ranged = combat?.weapon?.range !== null && combat?.weapon?.range !== undefined;
  const arrows = combat?.weapon?.arrows ?? 0;
  const engage: HudSlot = ranged
    ? state.rangedTarget
      ? {
          id: 'aim',
          key: 'a',
          label: 'Loose',
          meta: state.rangedTarget,
          group: 'verb',
          ...(arrows > 0 ? {} : { disabled: true }),
        }
      : {
          id: 'target',
          key: 't',
          label: 'Target',
          group: 'verb',
          ...(state.targeting
            ? { on: true, meta: 'choosing' }
            : arrows > 0
              ? { meta: combat?.weapon?.name ?? 'bow' }
              : { disabled: true, meta: 'no arrows' }),
        }
    : combat?.engagedWith
      ? {
          id: 'aim',
          key: 'a',
          label: 'Strike',
          group: 'verb',
          meta: state.engaged ?? 'opponent',
        }
      : {
          id: 'engage',
          key: 'z',
          label: 'Engage',
          group: 'verb',
          ...(state.adjacent
            ? { meta: state.adjacent }
            : { disabled: true, meta: 'nobody in reach' }),
        };

  const panels: HudSlot[] = [
    {
      id: 'examine',
      key: 'x',
      label: 'Look',
      group: 'panel',
      ...(state.looking ? { on: true } : {}),
    },
    {
      id: 'satchel',
      key: 'i',
      label: 'Satchel',
      group: 'panel',
      ...(state.satchelOpen ? { on: true } : {}),
    },
    {
      id: 'sheet',
      key: 'p',
      label: 'Character',
      group: 'panel',
      ...(state.sheetOpen ? { on: true } : {}),
    },
    {
      id: 'battle',
      key: 'v',
      label: 'Fight',
      group: 'panel',
      ...(state.battleOpen ? { on: true } : {}),
    },
  ];

  return [gather, light, camp, stalk, guard, engage, ...panels];
}

function capitalize(text: string): string {
  return text.slice(0, 1).toUpperCase() + text.slice(1);
}
